import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell, Radio, UserPlus, MessageCircle, Trophy, CalendarClock, Swords, CheckCheck, Gamepad2, Flag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import MemberModal from "@/components/community/MemberModal";

const ICONS = { stream_live: Radio, follow: UserPlus, comment: MessageCircle, stream_comment: MessageCircle,
  tournament: Trophy, match_schedule: CalendarClock, match_result: Swords, champion: Trophy,
  game_submission: Gamepad2, game_published: Gamepad2, game_rejected: Gamepad2, game_comment: MessageCircle, report: Flag };

function ago(iso) {
  const s = Math.max(1, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function Notifications() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [member, setMember] = useState(null);
  const closeMember = useCallback(() => setMember(null), []);
  const ref = useRef();
  const nav = useNavigate();

  const load = () => api.get("/notifications").then((r) => { setItems(r.data.items); setUnread(r.data.unread); }).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onDoc); };
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read").catch(() => {});
    setItems((cur) => cur.map((n) => ({ ...n, read: true }))); setUnread(0);
  };
  const openItem = (n) => {
    setOpen(false);
    if (!n.read) {
      api.post(`/notifications/${n.id}/read`).catch(() => {});
      setItems((cur) => cur.map((x) => (x.id === n.id ? { ...x, read: true } : x))); setUnread((u) => Math.max(0, u - 1));
    }
    if (n.type === "follow" && n.actor_id) return setMember(n.actor_id);
    nav(n.link || (n.stream_id ? `/community?stream=${n.stream_id}` : "/community"));
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} data-testid="notif-btn" className="relative p-2 rounded-md text-white/60 hover:text-white hover:bg-white/5 transition-colors">
        <Bell size={18} />
        {unread > 0 && <span data-testid="notif-badge" className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div className="fixed sm:absolute inset-x-2 sm:inset-x-auto top-16 sm:top-auto sm:right-0 sm:mt-2 sm:w-80 glass rounded-2xl overflow-hidden z-50" data-testid="notif-panel">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <span className="text-sm font-bold text-white">Notifications</span>
            <button onClick={markAll} disabled={unread === 0} data-testid="notif-mark-all" className="inline-flex items-center gap-1 text-[11px] font-bold text-[hsl(var(--primary))] disabled:text-white/30"><CheckCheck size={13} /> Mark all as read</button>
          </div>
          <div className="max-h-96 overflow-y-auto no-scrollbar">
            {items.length === 0 && <div className="p-6 text-sm text-white/40 text-center">No notifications yet.</div>}
            {items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <button key={n.id} onClick={() => openItem(n)} data-testid={`notif-item-${n.id}`}
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-white/5 transition ${!n.read ? "bg-white/[0.04]" : ""}`}>
                  <span className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${n.type === "stream_live" ? "bg-red-500/20 text-red-400" : "bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))]"}`}><Icon size={14} /></span>
                  <div className="min-w-0 flex-1"><div className={`text-sm leading-snug ${n.read ? "text-white/60" : "text-white"}`}>{n.text}</div><div className="text-[11px] text-white/40 mt-0.5">{ago(n.created_at)}</div></div>
                  {!n.read && <span className="w-2 h-2 mt-1.5 rounded-full bg-[hsl(var(--primary))] shrink-0" data-testid={`notif-unread-${n.id}`} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {member && <MemberModal userId={member} onClose={closeMember} />}
    </div>
  );
}
